import {
  File05Icon,
  InfoCircleIcon,
  Loading02Icon,
  Scales01Icon,
  XCloseIcon,
} from '@langchain/untitled-ui-icons';
import { FormControl, FormLabel } from '@mui/joy';

import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';

import { defaultChatModelManifest } from '@/Pages/Playground/utils/Playground.utils';
import { Dialog, DialogContent } from '@/components/Dialog/Dialog';
import { validateOutputSchemaKeys } from '@/components/EvaluatorCrudPane/utils/validateOutputSchemaKeys';
import {
  RulesFilterInput,
  RulesSamplingRateSlider,
} from '@/components/Rules/SessionRulesCrudFormV2';
import { SearchModel } from '@/components/RunsTable/types';
import useToast from '@/components/Toast/useToast';
import {
  Badge,
  Button,
  IconButton,
  Input,
  Text,
  Tooltip,
} from '@/design-system';
import { cn } from '@/design-system/utils/cn';
import { useRunRulesMutation, useSessions } from '@/hooks/useSwr';
import { useAlignmentStore } from '@/stores/useAlignmentStore';
import {
  MessageTuple,
  RuleMutateBody,
  SessionRuleMutateBody,
} from '@/types/schema';

interface CreateEvaluatorFromExperimentDataContentProps {
  isCreating: boolean;
  datasetId?: string;
  sessionId?: string;
  searchModel?: SearchModel;
  setSearchModel?: (model: SearchModel) => void;
  onClose: () => void;
}

function CreateEvaluatorFromExperimentDataContent({
  isCreating,
  datasetId,
  sessionId,
  searchModel,
  setSearchModel,
  onClose,
}: CreateEvaluatorFromExperimentDataContentProps) {
  'use no memo';
  const { setAlignmentMode, setAlignmentQueueId, setRunRuleId } =
    useAlignmentStore();
  const mutate = useRunRulesMutation(null, {
    onError: (error) => {
      createToast({
        title: 'Error creating evaluator',
        description: error.message,
        error: true,
      });
      setIsCreatingRule(false);
    },
  });
  const [feedbackKey, setFeedbackKey] = useState('');
  const [isCreatingRule, setIsCreatingRule] = useState(false);

  const prompt: MessageTuple[] = [
    ['system', '[Replace with grading instructions]'],
    [
      'user',
      `Please grade the following agent run given the input${
        datasetId ? ', expected output,' : ''
      } and actual output:\n\n<${
        datasetId ? 'example' : 'run'
      }>\n<input>\n{{input}}\n</input>\n\n<output>\n{{output}}\n</output>${
        datasetId
          ? '\n\n<referenceOutput>\n{{referenceOutput}}\n</referenceOutput>'
          : ''
      }\n</${datasetId ? 'example' : 'run'}>`,
    ],
  ];

  const variableMapping = {
    input: 'input',
    output: 'output',
    ...(datasetId && { referenceOutput: 'referenceOutput' }),
  };

  const handleClose = () => {
    onClose();
    setFeedbackKey('');
    setIsCreatingRule(false);
  };

  const { createToast } = useToast();

  const createPayload = (sessionId, datasetId) => {
    const isSessionBased = Boolean(sessionId);

    return {
      // Use dataset_id if datasetId exists, otherwise use session_id
      ...(datasetId ? { dataset_id: datasetId } : { session_id: sessionId }),

      is_enabled: false,

      // Session-based configurations
      sampling_rate: isSessionBased ? form.getValues('sampling_rate') : 1,
      filter: isSessionBased ? searchModel?.filter : 'eq(is_root, true)',
      tree_filter: isSessionBased ? searchModel?.treeFilter : undefined,
      trace_filter: isSessionBased ? searchModel?.traceFilter : undefined,

      // Common configurations
      display_name: feedbackKey,
      use_corrections_dataset: false,
      evaluator_version: 3,

      evaluators: [
        {
          structured: {
            model: defaultChatModelManifest,
            schema: {
              title: 'extract',
              description: "Extract information from the user's response.",
              type: 'object',
              properties: {
                [feedbackKey]: {
                  type: 'boolean',
                  description:
                    'True means the response met the grading criteria. False means the response did not meet the grading criteria.',
                },
                comment: {
                  type: 'string',
                  description: 'Reasoning for the score',
                },
              },
              required: [feedbackKey],
            },
            prompt: prompt,
            template_format: 'mustache',
            variable_mapping: variableMapping,
          },
        },
      ],

      create_alignment_queue: true,
    };
  };

  const handleCreateEvaluatorAndClose = async () => {
    setIsCreatingRule(true);
    try {
      if (!sessionId && !datasetId) {
        throw new Error('No dataset or session id provided');
      }
      const payload = createPayload(sessionId, datasetId);
      const newRule = await mutate.trigger({
        json: payload as RuleMutateBody,
      });
      if (newRule && newRule.alignment_annotation_queue_id) {
        // set alignment mode to true, alignment queue id and evaluator url
        setAlignmentMode(true);
        setAlignmentQueueId(newRule.alignment_annotation_queue_id);
        setRunRuleId(newRule.id);
      }
      handleClose();
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      setIsCreatingRule(false);
    }
  };

  const form = useForm<SessionRuleMutateBody>({
    defaultValues: {
      sampling_rate: 1,
    },
  });

  const { data } = useSessions(
    datasetId
      ? {
          reference_dataset: [datasetId],
          facets: false,
          limit: 1,
          include_stats: false,
        }
      : null,
    undefined,
    { revalidateOnFocus: false }
  );
  const experimentsOrRunsExist = datasetId
    ? (data?.rows ?? []).length > 0
    : !!sessionId;

  const inSessionCreationMode =
    isCreating && sessionId && searchModel && setSearchModel;
  const numberClassnames =
    'text-secondary shrink-0 font-semibold text-[12px] flex h-5 w-5 items-center justify-center rounded-full bg-quaternary';

  if (isCreatingRule) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <Loading02Icon className="h-6 w-6 animate-spin" />
        <Text className="text-lg">Creating evaluator...</Text>
      </div>
    );
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-start justify-between">
        <div className="rounded-full bg-brand-secondary p-3">
          <Scales01Icon className="h-6 w-6 text-brand-secondary" />
        </div>
        <IconButton
          onClick={handleClose}
          size="sm"
          color="secondary"
          variant="plain"
          icon={XCloseIcon}
          iconClassName="size-6"
          label="Close"
        />
      </div>
      <Text variant="h2" className="mb-1.5 mt-[14px]">
        {isCreating
          ? 'Create an evaluator aligned to your preferences'
          : 'Align this evaluator to your preferences'}
      </Text>
      <Text variant="body" className="text-tertiary">
        Make sure that your LLM-as-a-judge evaluator leaves the same feedback as
        you would.
      </Text>
      <div className="mt-4 flex shrink-0 flex-col gap-3 rounded-[12px] bg-tertiary p-4">
        <span className="text-sm text-quaternary">HOW IT WORKS</span>
        {inSessionCreationMode && (
          <div className="flex shrink-0 gap-4 text-sm">
            <div className={numberClassnames}>1</div>
            <Text variant="body" className="text-wrap">
              Set the run filters and sampling rate for your evaluator
            </Text>
          </div>
        )}
        <div className="flex shrink-0 gap-4 text-sm">
          <div className={numberClassnames}>
            {inSessionCreationMode ? '2' : '1'}
          </div>
          <Text variant="body" className="text-wrap">
            Select {datasetId ? 'experiments' : 'runs'} for human review
          </Text>
        </div>
        <div className="flex shrink-0 gap-4 text-sm">
          <div className={numberClassnames}>
            {inSessionCreationMode ? '3' : '2'}
          </div>
          <Text variant="body" className="text-wrap">
            Annotate the selected {datasetId && 'experiment'} runs with the
            expected evaluator score
          </Text>
        </div>
        <div className="flex shrink-0 gap-4 text-sm">
          <div className={numberClassnames}>
            {inSessionCreationMode ? '4' : '3'}
          </div>
          <Text variant="body" className="text-wrap">
            {isCreating ? (
              <>
                Create an evaluator prompt and test it against the expected
                evaluator scores in the Playground
              </>
            ) : (
              <>
                Test your evaluator prompt against the expected evaluator scores
                in the Playground
              </>
            )}
          </Text>
        </div>
        <div className="flex shrink-0 gap-4 text-sm">
          <div className={numberClassnames}>
            {inSessionCreationMode ? '5' : '4'}
          </div>
          <Text variant="body" className="text-wrap">
            Refine your prompt and repeat to improve scores over time
          </Text>
        </div>
      </div>
      <div className="items-top mt-6 flex gap-4">
        {isCreating && experimentsOrRunsExist && (
          <div
            className={cn(
              'flex flex-col gap-1',
              !inSessionCreationMode && 'w-full'
            )}
          >
            <FormLabel>Set feedback key</FormLabel>
            <Input
              size="md"
              onChange={(e) => {
                setFeedbackKey(e);
              }}
              value={feedbackKey}
              autoFocus
              placeholder="e.g. 'correctness'"
              className={cn(!inSessionCreationMode && 'w-full')}
            />
            {feedbackKey.trim() &&
              !validateOutputSchemaKeys([feedbackKey]).isValid && (
                <span className="mt-1 text-xs text-error">
                  Feedback name cannot contain whitespace or capital letters
                </span>
              )}
          </div>
        )}
        {inSessionCreationMode && (
          <>
            <div className="flex shrink-0 flex-col gap-1">
              <FormLabel>Run Filters</FormLabel>
              <div>
                <RulesFilterInput
                  sessionId={sessionId}
                  value={searchModel}
                  onChange={(model) => {
                    setSearchModel(model);
                  }}
                  className="h-[38px] shrink-0"
                />
              </div>
            </div>

            <FormControl sx={{ width: '185px' }}>
              <FormLabel>Sampling Rate</FormLabel>

              <Controller
                name="sampling_rate"
                control={form.control}
                render={({ field }) => (
                  <RulesSamplingRateSlider
                    value={field.value}
                    onChange={field.onChange}
                  />
                )}
              />
            </FormControl>
          </>
        )}
      </div>
      {experimentsOrRunsExist && (
        <div className="mt-8 flex items-center justify-between gap-2">
          <Link
            to="https://docs.langchain.com/langsmith/improve-judge-evaluator-feedback"
            target="_blank"
          >
            <Button variant="underlined" size="sm" leftDecorator={File05Icon}>
              Learn more in docs
            </Button>
          </Link>
          <div className="flex justify-end gap-2">
            {isCreating && (
              <Button
                variant="outlined"
                color="secondary"
                size="md"
                onClick={handleClose}
                className="px-3"
              >
                Cancel
              </Button>
            )}
            <Tooltip
              title={
                !feedbackKey.trim() ? 'Please set a feedback key' : undefined
              }
            >
              <span>
                <Button
                  variant="normal"
                  size="md"
                  onClick={
                    isCreating ? handleCreateEvaluatorAndClose : handleClose
                  }
                  className="px-3"
                  disabled={
                    isCreating &&
                    (!feedbackKey.trim() ||
                      !validateOutputSchemaKeys([feedbackKey]).isValid)
                  }
                >
                  <Text>Select {datasetId ? 'Experiments' : 'Runs'}</Text>
                </Button>
              </span>
            </Tooltip>
          </div>
        </div>
      )}
      {!experimentsOrRunsExist && (
        <div className="mt-3 flex justify-center gap-2">
          <Badge color="warning" rounded="sm" leftDecorator={InfoCircleIcon}>
            {`You haven't added any ${
              datasetId ? 'experiments' : 'runs'
            } yet. Add one to get started.`}
          </Badge>
        </div>
      )}
    </div>
  );
}

interface CreateEvaluatorFromExperimentDataDialogProps {
  open: boolean;
  onOpenChange: (isOpen: boolean) => void;
  isCreating: boolean;
  datasetId?: string;
  sessionId?: string;
  searchModel?: SearchModel;
  setSearchModel?: (model: SearchModel) => void;
}

export function CreateEvaluatorFromExperimentDataDialog({
  open,
  onOpenChange,
  isCreating,
  datasetId,
  sessionId,
  searchModel,
  setSearchModel,
}: CreateEvaluatorFromExperimentDataDialogProps) {
  const handleClose = () => {
    onOpenChange(false);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(open) => {
        if (!open) {
          handleClose();
        }
      }}
    >
      <DialogContent
        showClose={false}
        className="max-w-[550px] rounded-2xl border border-secondary bg-primary p-4"
      >
        <CreateEvaluatorFromExperimentDataContent
          isCreating={isCreating}
          datasetId={datasetId}
          sessionId={sessionId}
          searchModel={searchModel}
          setSearchModel={setSearchModel}
          onClose={handleClose}
        />
      </DialogContent>
    </Dialog>
  );
}
